import { AdjustmentFilter } from "@pixi/filter-adjustment";
import { HideAllLinesCommand } from "slotworks/commands/lines/hide-all-lines-command";
import { SetAllLinesEffects } from "slotworks/commands/lines/set-all-lines-effects";
import { DimType } from "slotworks/commands/symbols/dim-symbols-command";
import DimSymbolsCommand from "slotworks/commands/symbols/dim-symbols-command";
import { getWinningSymbols } from "slotworks/commands/symbols/get-winning-symbols-command";
import { HighlightSymbolsCommand } from "slotworks/commands/symbols/highlight-symbols-command";
import { SetAllSymbolEffects } from "slotworks/commands/symbols/reset-all-symbol-effects";
import { SetSymbolEffects } from "slotworks/commands/symbols/set-symbol-effects";
import Components from "slotworks/components/components";
import BaseAnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/base-animate-symbol-behaviour";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import BetModel from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import FreespinWinResult from "slotworks/model/gameplay/records/results/freespin-win-result";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import State from "slotworks/state-machine/states/state";
import { unique } from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import { HexToRGB } from "slotworks/utils/math/color";
import GoldenRockFooterComponent from "goldenrock/components/footer/golden-rock-footer-component";

export default class ShowWinLines extends State {

    public static SKIPPABLE: boolean = true;
    public static HIGHLIGHT_SYMBOLS: boolean = true;
    public static MULTILINE_ONLY: boolean = false;
    public static WIN_TICK_TIME: number = 750;
    public static END_DELAY: number = 0;
    public static BIG_WINS: boolean = false;

    // Instead of waiting for all symbols on a line to animate, a fixed delay is used between each line
    public static PER_LINE_DELAY: number = 0;
    // Adds a delay between each symbol animating
    public static PER_SYMBOL_DELAY: number = 0;
    // Delay to add to the end of all wins highlighting (if they highlight)
    public static MIN_DISPLAY_TIME: number = 0;
    // If true, animations which are already playing will not start again if they are told to play again
    public static OVERLAP_ANIMATIONS: boolean = false;

    // Adds tints / filters to lines
    public static ENABLE_LINE_EFFECTS: boolean = false;
    // Adds tints / filters to symbol behaviours
    public static ENABLE_BEHAVIOUR_EFFECTS: boolean = false;

    // TODO: Should be a function or hook instead of list of behaviour names
    public static APPLY_EFFECTS_TO_SYMBOL_BEHAVIOURS: string[] = [
        "AnimateOver",
        "ColorFrame"
    ];
    public static DEFAULT_TINT_COLOR: number = 0xffffff;
    public static LINE_COLORS: number[] = [];

    protected contract: Contract<void>;

    protected bigWinPending: boolean;

    private animatingSymbols: SymbolComponent[] = [];

    public onEnter() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        const totalWin = GameState.getCurrentGame().getCurrentTotalWin();

        // Instances the highlight is skipped
        if (this.hasOneLine() && this.onlyShowOnMultipleWinLines()) {
            Services.transaction.setWinnings(record.cashWon, totalWin, 0).execute();
            this.complete();
            return;
        }

        const raceFuncs: Array<() => Contract<any>> = [];

        // Win value celebration (big win, celeberateWin)
        raceFuncs.push(() => this.winValueCelebration());

        // Highlight symbols
        if (ShowWinLines.HIGHLIGHT_SYMBOLS) {
            raceFuncs.push(() => this.highlightWins());
        }

        //  Complete win sequence
        this.contract = new Sequence<void>([
            () => new Parallel<void>(raceFuncs),
            () => Contract.getTimeoutContract(ShowWinLines.END_DELAY),
            () => Contract.wrap(() => {
                this.onDisplayComplete();
                this.complete();
            })
        ]);

        this.contract.execute();
    }

    public onSkip() {
        if (ShowWinLines.SKIPPABLE) {
            if (!this.bigWinPending) {
                this.contract.cancel();
                this.onDisplayComplete();
                this.skip();
            }
        }
    }

    public onExit() {
        Components.matrix.resetSymbols();
    }

    protected winValueCelebration() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        const results = record.results;

        if (this.bigWinTriggered()) {
            return this.bigWin();
        } else if (this.allWinsAreSameSymbol() && this.isAutospinsOrFreespins()) {
            const result = results[0] as LineResult;
            if (result) {
                return Components.celebrateWin.singleTotalWin(record.cashWon, result);
            } else {
                return Contract.empty();
            }
        } else {
            return Components.celebrateWin.totalWin(record.cashWon);
        }
    }

    protected bigWinTriggered() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        return ShowWinLines.BIG_WINS && Components.bigWin.isBigWin(record.cashWon, BetModel.getTotalStake());
    }

    protected highlightWins(): Contract<void> {

        this.animatingSymbols = [];

        const record = GameState.getCurrentGame().getCurrentRecord();
        const results = record.results;

        const winQueue: Array<() => Contract<void>> = [];

        const colors = [...ShowWinLines.LINE_COLORS];

        const tweenColors = [];

        for (const result of results) {
            if (result instanceof SymbolWinResult && (result.cashWon > 0 || result.linkedResult instanceof FreespinWinResult)) {
                const color = colors.shift();
                tweenColors.push(color);

                winQueue.push(() => this.highlightWin(result, color));
            }
        }

        let lineAnimations: Array<() => Contract<void>> = [];

        if (winQueue.length) {
            lineAnimations = [
                () => Contract.wrap(() => this.playSymbolHighlightSounds()),
                () => DimSymbolsCommand(Components.matrix.getAllSymbols(), DimType.Highlight),
                ...winQueue,
                () => Contract.getTimeoutContract(ShowWinLines.MIN_DISPLAY_TIME)
            ];
        }

        let lineSequence: Contract<void>;
        if (ShowWinLines.PER_LINE_DELAY) {
            lineSequence = new Sequence<void>(lineAnimations);
        } else {
            lineSequence = new Parallel<void>(lineAnimations);
        }

        return lineSequence;
    }

    protected highlightWin(result: SymbolWinResult, color: number) {
        const symbolsToAnimate = this.setupSymbolsToAnimate(result);

        // Show line
        if (result instanceof LineResult) {
            const reverse = result.positions[0].x !== 0;
            Components.lines.highlight(result.line, reverse);

            if (!this.bigWinPending) {
                Services.sound.event(SoundEvent.win_line_N, result.line.toString());
            }
        }

        // Tint and filter effects
        const filters: Array<PIXI.Filter<any>> = this.getFilterEffects(color);
        if (ShowWinLines.ENABLE_LINE_EFFECTS) {
            if (result instanceof LineResult) {
                if (filters.length) {
                    Components.lines.filter(result.line, filters);
                } else {
                    Components.lines.tint(result.line, color);
                }
            }
        }
        if (ShowWinLines.ENABLE_BEHAVIOUR_EFFECTS) {
            SetSymbolEffects(symbolsToAnimate, color, filters, ShowWinLines.APPLY_EFFECTS_TO_SYMBOL_BEHAVIOURS);
        }

        const highlightSymbols = HighlightSymbolsCommand(symbolsToAnimate, true);

        if (ShowWinLines.PER_LINE_DELAY) {
            highlightSymbols.execute();
            return Contract.getTimeoutContract(ShowWinLines.PER_LINE_DELAY);
        }

        return highlightSymbols;
    }

    // Get correct symbols to animate, already setup with delays etc
    protected setupSymbolsToAnimate(result: SymbolWinResult) {
        const symbols = Components.matrix.getSymbolsFromPositions(result.positions);
        const symbolsToAnimate: SymbolComponent[] = [];

        let delay = 0;

        symbols.forEach((symbol) => {

            let shouldAnimate = false;

            if (ShowWinLines.OVERLAP_ANIMATIONS || this.noDelays()) {
                if (this.animatingSymbols.indexOf(symbol) === -1) {
                    shouldAnimate = true;
                }
            } else {
                shouldAnimate = !this.isSymbolAlreadyAnimating(symbol);
            }

            if (shouldAnimate) {
                symbolsToAnimate.push(symbol);
                this.setAnimationBehaviourDelay(symbol, delay);
            }

            delay += ShowWinLines.PER_SYMBOL_DELAY;
        });

        this.animatingSymbols.push(...symbolsToAnimate);

        return symbolsToAnimate;
    }

    protected playSymbolHighlightSounds() {
        const winningSymbols = getWinningSymbols(GameState.getCurrentGame().getCurrentRecord().results);
        const winningSymbolIDs = winningSymbols.map((symbol) => symbol.symbolId);
        const uniqueWinningSymbolIDs = unique(winningSymbolIDs);

        Services.sound.event(SoundEvent.highlight_all_wins);
        for (const symbolID of uniqueWinningSymbolIDs) {
            Services.sound.event(SoundEvent.symbol_ID_highlight, symbolID);
            let count = 0;
            for (const symbolToCount of winningSymbolIDs) {
                if (symbolID === symbolToCount) {
                    count++;
                }
            }
            Services.sound.event(SoundEvent.symbol_ID_MATCHES_highlight, symbolID, count.toString());
        }
    }

    protected onDisplayComplete() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        const totalWin = GameState.getCurrentGame().getCurrentTotalWin();

        if (ShowWinLines.ENABLE_BEHAVIOUR_EFFECTS) {
            SetAllSymbolEffects(ShowWinLines.DEFAULT_TINT_COLOR, this.getFilterEffects(ShowWinLines.DEFAULT_TINT_COLOR), ShowWinLines.APPLY_EFFECTS_TO_SYMBOL_BEHAVIOURS);
        }
        if (ShowWinLines.ENABLE_LINE_EFFECTS) {
            SetAllLinesEffects(ShowWinLines.DEFAULT_TINT_COLOR, this.getFilterEffects(ShowWinLines.DEFAULT_TINT_COLOR));
        }

        Components.matrix.resetSymbols();
        HideAllLinesCommand().execute();
        Components.celebrateWin.hide();
        Services.transaction.setWinnings(record.cashWon, totalWin, ShowWinLines.WIN_TICK_TIME).execute();
    }

    protected allWinsAreSameSymbol() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        const symbolTypes: string[] = [];
        for (const result of record.results) {
            if (result instanceof LineResult) {
                symbolTypes.push(result.symbolType);
            }
        }

        return symbolTypes.every((v) => v === symbolTypes[0]);
    }

    protected hasOneLine() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        const results = record.results;
        const lines = [];

        for (const result of results) {
            if (result instanceof LineResult) {
                lines.push(result.line);
            }
        }

        return lines.length <= 1;
    }

    protected isAutospinsOrFreespins() {
        const record = GameState.getCurrentGame().getCurrentRecord();
        return (record instanceof FreespinRecord) || AutoplayModel.isAutoPlaying();
    }

    protected onlyShowOnMultipleWinLines() {
        return !this.isAutospinsOrFreespins() && ShowWinLines.MULTILINE_ONLY;
    }

    protected bigWin(): Contract<boolean> {

        const gameplay = GameState.getCurrentGame();

        let winnings: number;
        if (gameplay.isCurrentRecordRoot()) {
            winnings = GameState.getCurrentGame().getTotalWin();
        } else {
            winnings = GameState.getCurrentGame().getCurrentRecord().cashWon;
        }
        const stake = BetModel.getTotalStake();

        SmartUIFlags.set(SmartUIFlag.NO_SKIP, true);

        this.bigWinPending = true;

        return new Contract<boolean>((resolve) => {

            Components.footer.showBigWinMessage();

            Components.bigWin.showWin(winnings, stake).then((result) => {
                this.bigWinPending = false;
                SmartUIFlags.set(SmartUIFlag.NO_SKIP, false);
                resolve(result);
            });
        });
    }

    protected getFilterEffects(color: number): Array<PIXI.Filter<any>> {
        try {
            const rgb = HexToRGB(color);
            const max = 2;
            const filters = [new AdjustmentFilter({
                red: rgb.r / 255 * max,
                green: rgb.g / 255 * max,
                blue: rgb.b / 255 * max,
                saturation: 3
            })];

            return filters;
        } catch (e) {
            return [];
        }
    }

    protected setAnimationBehaviourDelay(symbol: SymbolComponent, delay: number) {
        symbol.getBehavioursOfType(BaseAnimateSymbolBehaviour).forEach((behaviour) => {
            behaviour.delay = delay;
        });
    }

    protected isSymbolAlreadyAnimating(symbol: SymbolComponent) {
        let alreadyAnimating = false;
        symbol.getBehavioursOfType(BaseAnimateSymbolBehaviour).forEach((behaviour) => {
            if (behaviour.isAnimating()) {
                alreadyAnimating = true;
            }
        });

        return alreadyAnimating;
    }

    private noDelays() {
        return ShowWinLines.PER_SYMBOL_DELAY === 0 && ShowWinLines.PER_LINE_DELAY === 0;
    }
}
